/**
 * useMaterialStability
 *
 * Fetches market stability data for all unique material codes found in a product's
 * component tree (both standalone materials and nested sub-product materials).
 *
 * Replaces the second raw useEffect + useState waterfall in ModelPage.
 */

import { useState, useEffect } from 'react';
import { materialService } from '../services';
import { getNestedMaterialComponents } from '../utils/modelTransforms';

/**
 * @param {Array|null|undefined} components - Product components array from useProductDetail
 * @param {Object} subProductDetails - Map of { [product_ref_id]: detailObject }
 * @param {Object} materialsById - Map of { [material_id]: materialObject }
 * @returns {Object} Map of { [materialCode]: Array<{ country, stability }> }
 */
const useMaterialStability = (components, subProductDetails, materialsById) => {
  const [stabilityByCode, setStabilityByCode] = useState({});

  useEffect(() => {
    if (!components) {
      setStabilityByCode({});
      return;
    }

    const productComponents = components.filter(c => c.component_type === 'product');
    const materialComponents = components.filter(c => c.component_type === 'material');
    const nestedMaterialComponents = getNestedMaterialComponents(productComponents, subProductDetails);

    const materialCodes = Array.from(
      new Set(
        [...materialComponents, ...nestedMaterialComponents]
          .map(c => materialsById[c.material_id]?.code)
          .filter(Boolean)
      )
    );

    if (materialCodes.length === 0) {
      setStabilityByCode({});
      return;
    }

    let cancelled = false;

    const fetchAll = async () => {
      const next = {};
      await Promise.all(
        materialCodes.map(async (code) => {
          try {
            const data = await materialService.getMarketStability(code);
            next[code] = Array.isArray(data) ? data : [];
          } catch {
            next[code] = [];
          }
        })
      );
      if (!cancelled) setStabilityByCode(next);
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [components, subProductDetails, materialsById]);

  return stabilityByCode;
};

export default useMaterialStability;
